import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useLang } from "../../lib/i18n";
import { ArrowUpRight } from "lucide-react";
import { REGISTER_URL } from "./RegistrationPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactDialog({ open, onOpenChange }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contactDialog.validation"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success(t("contactDialog.success"));
      setForm({ name: "", email: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(t("contactDialog.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-dialog"
        className="max-w-xl bg-white border-2 border-black rounded-none p-8 sm:p-12"
      >
        <DialogHeader>
          <DialogTitle dir="auto" className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight leading-none">
            {t("contactDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <div data-testid="contact-photoday-notice" className="mt-5 border border-black/15 bg-[#FDFBF7] px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <p className="font-body text-[13px] leading-snug text-black/75 flex-1">
            Looking to register your child for Photo Day? Please use our dedicated registration portal instead.
          </p>
          <a
            href={REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-photoday-link"
            className="inline-flex items-center gap-1 self-start sm:self-auto font-body text-[10px] uppercase tracking-[0.2em] font-bold text-black underline underline-offset-4 hover:opacity-60 transition-opacity whitespace-nowrap"
          >
            Register for Photo Day <ArrowUpRight size={12} />
          </a>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="mt-6 grid grid-cols-1 gap-5">
          <Field id="contact-name" label={t("contactDialog.name")} value={form.name} onChange={onChange("name")} required />
          <Field id="contact-email" type="email" label={t("contactDialog.email")} value={form.email} onChange={onChange("email")} required />
          <div>
            <label
              htmlFor="contact-message"
              dir="auto"
              className="block font-body text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-2"
            >
              {t("contactDialog.message")}
              <span className="text-black ml-1">*</span>
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message"
              rows={4}
              required
              value={form.message}
              onChange={onChange("message")}
              dir="auto"
              className="w-full bg-transparent border-0 border-b border-black/25 focus:border-black focus:outline-none focus:ring-0 py-2 text-base font-body text-black placeholder:text-black/30 resize-none"
            />
          </div>

          <div className="mt-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              dir="auto"
              className="inline-flex items-center gap-2 bg-black text-white px-7 py-4 text-[11px] uppercase tracking-[0.25em] font-bold border border-black hover:bg-white hover:text-black transition-colors disabled:opacity-60"
            >
              {loading ? t("contactDialog.sending") : t("contactDialog.send")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ id, label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label
        htmlFor={id}
        dir="auto"
        className="block font-body text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-2"
      >
        {label}
        {required && <span className="text-black ml-1">*</span>}
      </label>
      <input
        id={id}
        data-testid={id}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        dir="auto"
        className="w-full bg-transparent border-0 border-b border-black/25 focus:border-black focus:outline-none focus:ring-0 py-2 text-base font-body text-black placeholder:text-black/30"
      />
    </div>
  );
}
